import { captureException } from "@freightsimple/observability/errorReporting";
import { reloadOnceForChunkError } from "./staleChunkRecovery";

export type BootStage = "init_parallel" | "dynamic_imports" | "render";

/**
 * Called when Hubble's startup chain rejects. React hasn't mounted, so no
 * error boundary can help: without this the page sits on the loader forever.
 * A stale chunk gets one reload; anything else is reported and the loader is
 * swapped for a message with a Reload button.
 */
export function handleBootFailure(error: unknown, stage: BootStage): void {
  if (reloadOnceForChunkError(error)) return;
  captureException(error, { "boot.stage": stage });
  showBootFailureMessage();
}

// Plain DOM: the loader lives in index.html, outside anything React renders.
function showBootFailureMessage(): void {
  drawBootFailureMessage();
  // index.html writes its loading line on DOMContentLoaded, and the module
  // script that boots Hubble runs before that event, so a failure that settles
  // right away would be overwritten. Its listener was added first, so ours
  // runs after it and draws the message again.
  if (document.readyState !== "complete") {
    document.addEventListener("DOMContentLoaded", drawBootFailureMessage, {
      once: true,
    });
  }
}

function drawBootFailureMessage(): void {
  const message = document.getElementById("initial-loader-message");
  if (!message) return;

  const text = document.createElement("p");
  text.textContent = "Hubble couldn't load. Check your connection and reload.";

  const reload = document.createElement("button");
  reload.type = "button";
  reload.dataset["testid"] = "boot-failure-reload-button";
  reload.textContent = "Reload";
  // A button doesn't inherit font or colour, and index.css may not have
  // loaded, so take both from the loader line.
  reload.style.font = "inherit";
  reload.style.color = "inherit";
  reload.style.background = "transparent";
  reload.style.marginTop = "8px";
  reload.style.padding = "4px 12px";
  reload.style.border = "1px solid currentColor";
  reload.style.borderRadius = "6px";
  reload.style.cursor = "pointer";
  reload.addEventListener("click", () => window.location.reload());

  message.replaceChildren(text, reload);
}
